import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";

/**
 * Excel external-workbook references look like [Filename.xlsx]Sheet!Cell
 * or '[Filename.xlsx]Sheet'!Cell. Flag any formula containing that pattern
 * — these break silently when the source workbook moves or isn't open.
 */
const EXTERNAL_LINK_RE = /\[[^\]]+\.(?:xlsx|xlsm|xlsb|xls|csv)\]/i;

export const externalLinks: AuditRule = {
  id: "system.audit.external-links",
  name: "External workbook links",
  description:
    "Formulas referencing other workbook files. These break when the source workbook is moved, renamed, or unavailable.",
  enabled: true,
  source: "system",
  severity: "warning",
  /**
   * Per-formula-pattern cache stores the matched filename (or null if
   * the pattern doesn't link externally). The same external-link
   * bracket appears identically in A1 and R1C1 forms, so the
   * predicate works on R1C1 strings unchanged.
   *
   * At 1M cells with ~5k distinct formulas, ~5k regex evaluations
   * instead of 1M.
   */
  check(sheet, model) {
    const findings: AuditFindingPartial[] = [];
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const r1c1Strings = model.catalogs.r1c1Strings;
    const r1c1Ids = sheet.r1c1Ids;

    // Cache value: the matched filename if the pattern links externally,
    // null if it doesn't. Map.has() distinguishes "uncomputed" from
    // "computed-as-null".
    const matchByPattern = new Map<number, string | null>();

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const id = r1c1Ids[rowBase + c];
        if (id < 0) continue;

        let matched = matchByPattern.get(id);
        if (matched === undefined && !matchByPattern.has(id)) {
          const m = r1c1Strings[id].match(EXTERNAL_LINK_RE);
          matched = m ? m[0] : null;
          matchByPattern.set(id, matched);
        }
        if (!matched) continue;

        findings.push({
          sheetName: sheet.name,
          address: addressFromRC(startRow + r, startCol + c),
          message: `external link: ${matched}`,
        });
      }
    }
    return findings;
  },
};
